import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
  Sparkles, Camera, Users, ArrowRight, Check, Layers, Bell,
  Globe, Trophy, Lock, ChevronDown,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { useI18n } from '../lib/i18n';

const FLAGS_MARQUEE = [
  '🇲🇽','🇦🇷','🇧🇷','🇺🇸','🇨🇦','🇬🇧','🇫🇷','🇩🇪','🇪🇸','🇳🇱','🇧🇪','🇵🇹','🇺🇾','🇨🇴',
  '🇯🇵','🇰🇷','🇦🇺','🇿🇦','🇨🇿','🇨🇭','🇦🇹','🇹🇷','🇭🇷','🇲🇦','🇸🇳','🇪🇬','🇨🇮','🇬🇭',
  '🇩🇿','🇮🇷','🇮🇶','🇯🇴','🇶🇦','🇸🇦','🇺🇿','🇪🇨','🇵🇾','🇵🇦','🇭🇹','🇨🇻','🇨🇩','🇨🇼',
  '🇧🇦','🏴󠁧󠁢󠁳󠁣󠁴󠁿','🇸🇪','🇳🇴','🇹🇳','🇳🇿',
];

function AnimatedCounter({ to, duration = 1.6 }: { to: number; duration?: number }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v).toLocaleString('es'));
  useEffect(() => {
    const controls = animate(count, to, { duration, ease: 'easeOut' });
    return controls.stop;
  }, [count, to, duration]);
  return <motion.span>{rounded}</motion.span>;
}

export function Landing() {
  const { t, lang, setLang } = useI18n();
  return (
    <div className="min-h-dvh relative overflow-hidden bg-ink">
      {/* Sticky nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-ink/80 border-b border-white/8">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2">
            <Logo size={28} />
            <span className="font-display font-extrabold text-base hidden sm:block">{t('landing.nav.brand')}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <div className="flex rounded-full bg-white/5 border border-white/10 p-0.5 text-2xs font-bold">
              <button
                onClick={() => setLang('es')}
                className={`px-2.5 py-1 rounded-full transition ${lang === 'es' ? 'bg-flame text-white shadow-glow-flame' : 'text-haze hover:text-foam'}`}
              >
                ES
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-2.5 py-1 rounded-full transition ${lang === 'en' ? 'bg-flame text-white shadow-glow-flame' : 'text-haze hover:text-foam'}`}
              >
                EN
              </button>
            </div>
            <Link to="/login" className="btn-ghost text-sm py-2 hidden sm:inline-flex">{t('landing.cta.signIn')}</Link>
            <Link to="/login" className="btn-primary text-sm py-2">
              <Sparkles size={14} /> {t('landing.cta.start')}
            </Link>
          </div>
        </div>
      </header>

      {/* HERO — impactful */}
      <section className="relative overflow-hidden">
        {/* Background layers */}
        <div className="atmosphere atmosphere--hero" />
        <div className="absolute inset-0 bg-grid -z-10 opacity-50" />
        {/* Pulsing radial glow detrás */}
        <motion.div
          aria-hidden
          initial={{ opacity: 0.3 }}
          animate={{ opacity: [0.35, 0.65, 0.35] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 h-[700px] w-[700px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(94,224,255,0.18) 0%, rgba(230,0,18,0.12) 30%, transparent 70%)',
            filter: 'blur(48px)',
          }}
        />

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-12 sm:pt-20 pb-12 text-center">
          {/* Pill que respira */}
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="chip inline-flex border-cyan/40 bg-cyan/10 text-cyan mb-6"
          >
            <motion.span
              className="dot"
              animate={{ scale: [1, 1.4, 1], boxShadow: ['0 0 8px rgba(94,224,255,0.5)', '0 0 18px rgba(94,224,255,1)', '0 0 8px rgba(94,224,255,0.5)'] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ background: '#5ee0ff' }}
            />
            {t('landing.hero.pill')}
          </motion.span>

          {/* MEGA TITLE */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="h-display font-extrabold leading-[0.85] tracking-[-0.05em] text-balance"
            style={{ fontSize: 'clamp(3rem, 11vw, 8rem)' }}
          >
            <span className="block">{t('landing.hero.t1')}</span>
            <span className="block gradient-text relative">
              {t('landing.hero.t2')}
              <motion.span
                aria-hidden
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', repeatDelay: 2 }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)',
                  mixBlendMode: 'overlay',
                }}
              />
            </span>
            <span className="block">{t('landing.hero.t3')}</span>
            <span className="block text-flame">{t('landing.hero.t4')}</span>
          </motion.h1>

          {/* Subcopy */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mx-auto max-w-2xl text-haze text-base sm:text-xl mt-8 text-balance"
          >
            {t('landing.hero.subcopy')}{' '}
            <span className="text-foam font-semibold">{t('landing.hero.subcopy.bold')}</span>
          </motion.p>

          {/* Live counter — "X láminas detectadas hoy" */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-8 inline-flex items-center gap-3 rounded-full px-5 py-2.5 glass border border-lime/30"
          >
            <motion.span
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              className="text-lime"
            >
              <Sparkles size={14} />
            </motion.span>
            <span className="text-sm text-haze">
              <span className="font-display font-extrabold text-lime text-lg">
                <AnimatedCounter to={980} />
              </span>{' '}
              {t('landing.hero.counter.suffix')} · <span className="text-foam font-semibold">{t('landing.hero.counter.countries')}</span>
            </span>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center"
          >
            <Link
              to="/login"
              className="btn-primary text-base px-8 py-4 w-full sm:w-auto relative overflow-hidden group"
            >
              <span className="relative z-10 flex items-center gap-2">
                {t('landing.cta.start')} <ArrowRight size={16} />
              </span>
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)',
                }}
              />
            </Link>
            <a href="#how" className="btn-ghost text-base px-8 py-4 w-full sm:w-auto">
              {t('landing.cta.howItWorks')}
            </a>
          </motion.div>

          {/* Stats inline */}
          <div className="pt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-2xs uppercase tracking-widest text-haze">
            <Stat icon={<Check className="text-lime" size={14} />} label={t('landing.stats.noStore')} />
            <Stat icon={<Lock className="text-cyan" size={14} />} label={t('landing.stats.googleLogin')} />
            <Stat icon={<Trophy className="text-gold" size={14} />} label={t('landing.stats.tradeNotifs')} />
          </div>

          {/* Live demo mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="relative mt-14 sm:mt-20 flex justify-center"
          >
            <PhoneDemo />
          </motion.div>
        </div>

        {/* Marquee de banderas — infinito */}
        <div className="relative py-4 border-y border-white/8 bg-ink/40 overflow-hidden">
          <motion.div
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
            className="flex gap-5 text-3xl whitespace-nowrap"
          >
            {[...FLAGS_MARQUEE, ...FLAGS_MARQUEE].map((f, i) => (
              <span key={i} className="opacity-60 hover:opacity-100 transition">{f}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS (3 steps) */}
      <section id="how" className="relative mx-auto max-w-6xl px-4 sm:px-6 pb-20">
        <div className="atmosphere atmosphere--flame" />
        <SectionHeader
          eyebrow={t('landing.how.eyebrow')}
          title={t('landing.how.title')}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <Step n={1} icon={<Camera size={24} />} tone="flame"
            title={t('landing.how.s1.title')}
            body={t('landing.how.s1.body')} />
          <Step n={2} icon={<Layers size={24} />} tone="lime"
            title={t('landing.how.s2.title')}
            body={t('landing.how.s2.body')} />
          <Step n={3} icon={<Users size={24} />} tone="cyan"
            title={t('landing.how.s3.title')}
            body={t('landing.how.s3.body')} />
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="relative mx-auto max-w-6xl px-4 sm:px-6 pb-20">
        <div className="atmosphere atmosphere--royal" />
        <SectionHeader
          eyebrow={t('landing.diff.eyebrow')}
          title={t('landing.diff.title')}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Feature icon={<Camera />} tone="flame"
            title={t('landing.diff.f1.title')}
            body={t('landing.diff.f1.body')} />
          <Feature icon={<Bell />} tone="cyan"
            title={t('landing.diff.f2.title')}
            body={t('landing.diff.f2.body')} />
          <Feature icon={<Globe />} tone="lime"
            title={t('landing.diff.f3.title')}
            body={t('landing.diff.f3.body')} />
          <Feature icon={<Trophy />} tone="gold"
            title={t('landing.diff.f4.title')}
            body={t('landing.diff.f4.body')} />
        </div>
      </section>

      {/* QUOTE / SOCIAL VIBE */}
      <section className="relative mx-auto max-w-3xl px-4 sm:px-6 pb-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass-strong rounded-3xl p-8 sm:p-12 relative overflow-hidden"
        >
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-flame/20 blur-2xl" />
          <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-cyan/15 blur-2xl" />
          <span className="text-flame text-5xl font-display leading-none">"</span>
          <p
            className="font-display font-extrabold text-xl sm:text-2xl mt-2 leading-tight text-balance [&_em]:not-italic [&_em]:text-gradient"
            dangerouslySetInnerHTML={{ __html: t('landing.quote.body') }}
          />
          <p className="mt-4 text-sm text-haze">
            {t('landing.quote.author')}
          </p>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="relative mx-auto max-w-3xl px-4 sm:px-6 pb-20">
        <SectionHeader eyebrow={t('landing.faq.eyebrow')} title={t('landing.faq.title')} />
        <div className="space-y-2">
          <FAQ q={t('landing.faq.q1.q')} html={t('landing.faq.q1.a')} />
          <FAQ q={t('landing.faq.q2.q')} html={t('landing.faq.q2.a')} />
          <FAQ q={t('landing.faq.q3.q')} html={t('landing.faq.q3.a')} />
          <FAQ q={t('landing.faq.q4.q')} html={t('landing.faq.q4.a')} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative mx-auto max-w-5xl px-4 sm:px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl glass-strong p-8 sm:p-12 text-center"
        >
          <div className="atmosphere atmosphere--mix" />
          <h2 className="h-display text-4xl sm:text-5xl text-balance leading-[0.95]">
            {t('landing.finalCta.t1')}<br /><span className="gradient-text">{t('landing.finalCta.t2')}</span>
          </h2>
          <p className="mt-4 text-haze text-base sm:text-lg max-w-xl mx-auto">
            {t('landing.finalCta.subcopy')}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link to="/login" className="btn-primary text-base px-8 py-4 w-full sm:w-auto">
              {t('landing.cta.start')} <ArrowRight size={16} />
            </Link>
            <Link to="/register" className="btn-ghost text-base px-8 py-4 w-full sm:w-auto">
              {t('landing.finalCta.cta2')}
            </Link>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-white/8 py-8 text-center text-xs text-haze">
        {t('landing.footer')}
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────────────
function Stat({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold">
      {icon}
      {label}
    </span>
  );
}

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="text-center mb-10">
      <p className="text-2xs uppercase tracking-[0.3em] text-haze font-bold">{eyebrow}</p>
      <h2 className="h-display text-3xl sm:text-4xl mt-2 text-balance">{title}</h2>
    </div>
  );
}

function Step({ n, icon, tone, title, body }: {
  n: number; icon: React.ReactNode; tone: 'flame'|'lime'|'cyan';
  title: string; body: string;
}) {
  const t = tone === 'flame'
    ? { ring: 'border-flame/35 bg-flame/10', icon: 'text-flame', shadow: 'shadow-glow-flame' }
    : tone === 'lime'
      ? { ring: 'border-lime/35 bg-lime/10', icon: 'text-lime', shadow: 'shadow-glow-lime' }
      : { ring: 'border-cyan/35 bg-cyan/10', icon: 'text-cyan', shadow: 'shadow-glow-cyan' };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: n * 0.05 }}
      className={`relative glass rounded-3xl p-6 ${t.shadow} overflow-hidden`}
    >
      <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full opacity-30 blur-2xl"
        style={{
          background: tone === 'flame' ? '#E60012' : tone === 'lime' ? '#C6FF5E' : '#5EE0FF',
        }}
      />
      <div className="flex items-start justify-between mb-3">
        <div className={`inline-grid h-12 w-12 place-items-center rounded-2xl border ${t.ring} ${t.icon}`}>
          {icon}
        </div>
        <span className="font-display font-extrabold text-4xl text-haze/30 leading-none">
          0{n}
        </span>
      </div>
      <h3 className="font-display font-extrabold text-lg">{title}</h3>
      <p className="mt-2 text-sm text-haze leading-relaxed">{body}</p>
    </motion.div>
  );
}

function Feature({ icon, tone, title, body }: {
  icon: React.ReactElement; tone: 'flame'|'lime'|'cyan'|'gold';
  title: string; body: string;
}) {
  const t = {
    flame: { ring: 'border-flame/35 bg-flame/10', icon: 'text-flame' },
    lime:  { ring: 'border-lime/35 bg-lime/10',   icon: 'text-lime' },
    cyan:  { ring: 'border-cyan/35 bg-cyan/10',   icon: 'text-cyan' },
    gold:  { ring: 'border-gold/35 bg-gold/10',   icon: 'text-gold' },
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="glass rounded-3xl p-6 flex gap-4 items-start"
    >
      <div className={`shrink-0 inline-grid h-12 w-12 place-items-center rounded-2xl border ${t.ring} ${t.icon}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="font-display font-extrabold text-lg">{title}</h3>
        <p className="mt-1.5 text-sm text-haze leading-relaxed">{body}</p>
      </div>
    </motion.div>
  );
}

function FAQ({ q, html, children }: { q: string; html?: string; children?: React.ReactNode }) {
  return (
    <details className="group rounded-2xl glass overflow-hidden">
      <summary className="flex items-center justify-between cursor-pointer p-4 list-none">
        <h3 className="font-display font-bold text-base text-foam pr-4">{q}</h3>
        <ChevronDown
          size={18}
          className="shrink-0 text-haze transition-transform group-open:rotate-180"
        />
      </summary>
      {html ? (
        <div
          className="px-4 pb-4 text-sm text-haze leading-relaxed"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <div className="px-4 pb-4 text-sm text-haze leading-relaxed">{children}</div>
      )}
    </details>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PhoneDemo — mock animado del app (loop infinito)
// Cicla por 3 escenas: scanning → trade → completion
// ─────────────────────────────────────────────────────────────────────
function PhoneDemo() {
  const [scene, setScene] = useState<0 | 1 | 2>(0);
  const { t } = useI18n();

  useEffect(() => {
    const timings = [4200, 4200, 3000]; // ms por escena
    const t = setTimeout(() => setScene(((scene + 1) % 3) as 0 | 1 | 2), timings[scene]);
    return () => clearTimeout(t);
  }, [scene]);

  return (
    <div className="relative">
      {/* Glow halo detrás del phone */}
      <div
        className="pointer-events-none absolute -inset-10 rounded-full opacity-40 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(94,224,255,0.35) 0%, rgba(255,61,107,0.3) 30%, transparent 70%)',
        }}
      />
      {/* Sticker flotante decorativo izquierda */}
      <motion.div
        animate={{ y: [-6, 6, -6], rotate: [-8, -4, -8] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -left-16 sm:-left-24 top-8 z-0 hidden sm:block"
      >
        <div className="w-20 aspect-[3/4] rounded-xl slot-filled grid place-items-center font-display font-extrabold text-2xl text-foam">
          7
        </div>
      </motion.div>
      {/* Sticker flotante derecha */}
      <motion.div
        animate={{ y: [6, -6, 6], rotate: [8, 4, 8] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -right-12 sm:-right-20 bottom-12 z-0 hidden sm:block"
      >
        <div className="w-16 aspect-[3/4] rounded-xl holo-foil shadow-glow-gold grid place-items-center font-display font-extrabold text-xl text-foam">
          88
        </div>
      </motion.div>

      {/* Phone frame */}
      <div
        className="relative w-[280px] sm:w-[320px] rounded-[2.5rem] overflow-hidden border-[6px] border-white/15 bg-ink"
        style={{
          aspectRatio: '9/19',
          boxShadow:
            '0 50px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 1px rgba(255,255,255,0.1)',
        }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-30 w-24 h-5 bg-ink rounded-b-2xl" />

        {/* Status bar */}
        <div className="absolute top-1 inset-x-0 z-20 flex items-center justify-between px-6 text-2xs font-mono text-foam/70">
          <span>9:41</span>
          <span>100%</span>
        </div>

        {/* Screen content */}
        <div className="absolute inset-0 pt-6">
          {scene === 0 && <SceneScanning t={t} />}
          {scene === 1 && <SceneMatch t={t} />}
          {scene === 2 && <SceneComplete t={t} />}
        </div>

        {/* Indicador de escena */}
        <div className="absolute bottom-3 inset-x-0 z-20 flex items-center justify-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === scene ? 'w-6 bg-flame' : 'w-1.5 bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Escena 1: IA detectando una página
function SceneScanning({ t }: { t: (k: any) => string }) {
  const slots = [0, 1, 2, 3, 4, 5, 6, 7, 8];
  return (
    <motion.div
      key="scan"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full px-4 py-3 flex flex-col"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">🇦🇷</span>
        <div>
          <div className="font-display font-extrabold text-sm leading-tight">Argentina</div>
          <div className="text-[9px] text-haze">{t('landing.scene.detection')}</div>
        </div>
        <span className="ml-auto chip text-[8px] border-cyan/40 bg-cyan/15 text-cyan py-0.5 px-1.5">
          {t('landing.scene.analyzing')}
        </span>
      </div>

      {/* Grid de slots */}
      <div className="grid grid-cols-3 gap-1.5 flex-1">
        {slots.map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0.9, opacity: 0.3 }}
            animate={{
              scale: 1,
              opacity: 1,
              borderColor: ['rgba(255,255,255,0.15)', 'rgba(198,255,94,0.55)'],
              backgroundColor: ['rgba(255,255,255,0.02)', 'rgba(198,255,94,0.12)'],
              boxShadow: [
                '0 0 0px rgba(198,255,94,0)',
                '0 0 16px rgba(198,255,94,0.5)',
              ],
            }}
            transition={{
              duration: 0.4,
              delay: 0.4 + i * 0.18,
              ease: 'easeOut',
            }}
            className="relative aspect-[3/4] rounded-lg border-2 grid place-items-center"
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 + i * 0.18, duration: 0.3 }}
            >
              <Check size={14} className="text-lime" strokeWidth={3} />
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Counter abajo */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="mt-2 glass rounded-lg p-2 flex items-center justify-between"
      >
        <span className="text-[9px] text-haze uppercase tracking-wider">{t('landing.scene.detected')}</span>
        <span className="font-display font-extrabold text-lime tabular-nums text-base">
          9/20 ✓
        </span>
      </motion.div>
    </motion.div>
  );
}

// Escena 2: Match con otro user
function SceneMatch({ t }: { t: (k: any) => string }) {
  return (
    <motion.div
      key="match"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="h-full px-3 py-3 flex flex-col gap-2"
    >
      <div>
        <div className="text-[9px] uppercase tracking-widest text-haze font-bold">{t('landing.scene.match.label')}</div>
        <div className="font-display font-extrabold text-base">85%</div>
      </div>

      {/* VS */}
      <div className="rounded-xl glass p-3 flex items-center justify-between">
        <div className="text-center">
          <div className="h-9 w-9 rounded-full bg-lime/20 border-2 border-lime/60 grid place-items-center font-bold text-lime text-xs mx-auto">
            T
          </div>
          <div className="text-[8px] text-haze mt-0.5">{t('landing.scene.match.you')}</div>
        </div>
        <motion.div
          animate={{ rotate: [0, 180, 360] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="text-flame"
        >
          ⇄
        </motion.div>
        <div className="text-center">
          <div className="h-9 w-9 rounded-full bg-flame/20 border-2 border-flame/60 grid place-items-center font-bold text-flame text-xs mx-auto">
            M
          </div>
          <div className="text-[8px] text-haze mt-0.5">María</div>
        </div>
      </div>

      {/* Cards intercambio */}
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="rounded-lg bg-lime/10 border border-lime/40 p-2 text-center">
          <div className="text-[8px] uppercase tracking-widest text-lime font-bold">{t('landing.scene.match.iGive')}</div>
          <div className="mt-1 flex flex-wrap gap-1 justify-center">
            {['ARG 5', 'BRA 12', 'FRA 3'].map((c) => (
              <span key={c} className="text-[8px] font-mono bg-ink/40 px-1 py-0.5 rounded">{c}</span>
            ))}
          </div>
        </div>
        <div className="rounded-lg bg-cyan/10 border border-cyan/45 p-2 text-center">
          <div className="text-[8px] uppercase tracking-widest text-cyan font-bold">{t('landing.scene.match.iGet')}</div>
          <div className="mt-1 flex flex-wrap gap-1 justify-center">
            {['HAI 7', 'MEX 14', 'JPN 9'].map((c) => (
              <span key={c} className="text-[8px] font-mono bg-ink/40 px-1 py-0.5 rounded">{c}</span>
            ))}
          </div>
        </div>
      </div>

      <motion.button
        animate={{ scale: [1, 1.03, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        className="btn-primary text-xs py-2 w-full"
      >
        {t('landing.scene.match.cta')}
      </motion.button>
    </motion.div>
  );
}

// Escena 3: Álbum completo, confetti vibe
function SceneComplete({ t: _t }: { t: (k: any) => string }) {
  return (
    <motion.div
      key="done"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="h-full grid place-items-center text-center px-4"
    >
      <div>
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            filter: ['drop-shadow(0 0 0 rgba(198,255,94,0))', 'drop-shadow(0 0 30px rgba(198,255,94,0.8))', 'drop-shadow(0 0 0 rgba(198,255,94,0))'],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="text-7xl mb-3"
        >
          🏆
        </motion.div>
        <div className="font-display font-extrabold text-2xl gradient-text leading-tight">
          100% ✓
        </div>
        <div className="text-2xs text-haze mt-1">980 / 980</div>
        <div className="mt-4 flex justify-center gap-1.5">
          {['🇲🇽','🇦🇷','🇧🇷','🇫🇷','🇪🇸'].map((f) => (
            <motion.span
              key={f}
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: Math.random() * 0.6 }}
              className="text-base"
            >
              {f}
            </motion.span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function ShowcaseCard({
  number, rarity, rotation, offset, highlight,
}: { number: number; rarity: 'common'|'shiny'|'legend'; rotation: number; offset: number; highlight?: boolean }) {
  return (
    <motion.div
      animate={{ y: highlight ? [-6, 6, -6] : [-3, 3, -3] }}
      transition={{ duration: highlight ? 4 : 6, repeat: Infinity, ease: 'easeInOut' }}
      style={{
        transform: `translateX(${offset}px) rotate(${rotation}deg)`,
        zIndex: highlight ? 10 : 1,
      }}
      className={`absolute left-1/2 top-0 -translate-x-1/2 w-36 aspect-[3/4] rounded-2xl overflow-hidden ${
        rarity === 'legend' ? 'holo-foil shadow-glow-gold' :
        rarity === 'shiny'  ? 'holo-foil shadow-glow-cyan' :
                              'slot-filled'
      } ${highlight ? 'scale-110' : ''}`}
    >
      <div className="num-badge">#{number}</div>
      <div className="absolute inset-0 grid place-items-center font-display font-extrabold text-5xl text-foam">
        {number}
      </div>
      {rarity === 'legend' && (
        <div className="absolute top-1 left-1 z-10 rounded-md bg-gold px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider text-ink">
          ★ Legend
        </div>
      )}
      {rarity === 'shiny' && (
        <div className="absolute top-1 left-1 z-10 rounded-md bg-cyan px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider text-ink">
          ◆ Shiny
        </div>
      )}
    </motion.div>
  );
}
